/** Attribute schema — reference src/data/attribute-schema.ts */

import { AMAZON_BUSINESS_UNIT_OPTIONS } from '@/lib/dd-queue/data/amazonBusinessUnits'
import { canonicalizeMulti, splitMulti } from '@/lib/dd-queue/attributeMulti'
import { SIGN_BUILDER_MINIMUM_QUANTITY_PRESETS } from '@/types/salesWorkflow'
import { WHITE_INK_LABELS, formatMountingHoles } from '@/lib/dd-queue/finishingFromSpec'
import {
  GRAPHIC_METHODS,
  GROMMET_OPTIONS,
  HEM_OPTIONS,
  HOLE_DIAMETER_OPTIONS,
  HOLE_LOCATION_OPTIONS,
  LAMINATES,
  ROUNDED_CORNER_OPTIONS,
  SIGN_CATEGORIES,
  TAPING_OPTIONS,
  VINYL_TYPES,
  type MockCatalogItem,
} from '@/data/mockSignBuilderCatalog'

/**
 * DD-ATTR-OPTIONS-001 — the option lists are the Sign Builder's own (imports, never copies),
 * spelled exactly as the intake fill stores them, so a value from a real submission is always
 * one of the options. Taylor 2026-09-16: Sign Builder lists + "Other…" free text.
 */
const nonEmpty = (list: readonly string[]): string[] => list.filter((v) => v !== '')

/** '0.25' → '0.25"' — the wording buildOriginSpec stores; "No" and "Custom…" are not options. */
const ROUNDED_CORNER_VALUES: string[] = ROUNDED_CORNER_OPTIONS.filter(
  (o) => o.value !== 'none' && o.value !== 'custom'
).map((o) => `${o.value}"`)

/** Every location × (no diameter | each preset diameter) in the stored wording — 35 options. */
const MOUNTING_HOLE_VALUES: string[] = HOLE_LOCATION_OPTIONS.filter((l) => l.value !== 'none').flatMap(
  (l) => [
    formatMountingHoles(l.value, ''),
    ...HOLE_DIAMETER_OPTIONS.filter((d) => d.value !== 'custom').map((d) =>
      formatMountingHoles(l.value, d.value)
    ),
  ]
)

/**
 * F-038 (Amanda): the exact PIQ code of every option that carries one, keyed by the option
 * label (the stored value). Shown next to the label and searched with it; the stored value
 * stays the label. "None" and items without a code are simply absent.
 */
function codesOf(items: readonly MockCatalogItem[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const item of items) {
    if (item.piq) out[item.label] = item.piq
  }
  return out
}

export type AttributeSource = 'auto-intake' | 'auto-customer' | 'manual'

/**
 * Catalog-backed attribute (DD-SUBSTRATE-RESOLVE-001 / F-057): the value is a
 * Sign Builder catalog LABEL and a companion `<key>_id` attribute carries the
 * catalog id (= PIQ code today, F-038). Edited via a searchable picker, never
 * free text.
 */
export type AttributeCatalog = 'substrate'

export interface AttributeField {
  key: string
  label: string
  source: AttributeSource
  values?: string[]
  catalog?: AttributeCatalog
  /**
   * DD-AMZ-BU-001 — several picks from `values`, stored as one "; "-joined string
   * (attributeMulti.ts). Rendered as a searchable multi-select in Triage / Edit Line.
   */
  multi?: boolean
  /**
   * DD-ATTR-OPTIONS-001 — stored values from an earlier wording of `values`, mapped to the
   * current option so old lines keep showing and stay selectable (legacy → current).
   */
  aliases?: Record<string, string>
  /**
   * DD-ATTR-OPTIONS-001 — the list is a suggestion, not a rule: the control offers "Other…"
   * and any typed value is kept. Strict lists (locations, Vended, Standardization Type) do
   * not set this.
   */
  allowOther?: boolean
  /**
   * F-038 — option label → exact PIQ code (Laminate, Vinyl Type). The code is displayed next
   * to the value in the Attributes card and the pickers, and is searchable; it is never the
   * stored value. Substrates carry their code through the catalog id instead
   * (substrateCatalogOptions.substrateCode).
   */
  codes?: Record<string, string>
  /**
   * F-064 — the list offered on a New Product line instead of `values` (Standardization
   * Type: a New Product only ever becomes Standard or Standard-Custom). `values` stays the
   * full list for other line kinds; a value stored on the line outside this list is still
   * shown and selectable there (attributeSectionsFor).
   */
  newProductValues?: string[]
}

/** F-064 (Taylor) — what a New Product Request can end up as. */
export const NEW_PRODUCT_STD_TYPE_VALUES: readonly string[] = ['Standard', 'Standard-Custom']

/**
 * DD-LOC-RULES-001 (F-032 Amanda / F-028 Jessica) — the location values. "Both Locations" is
 * the Production Location default (either plant can build it). "Design TSP" is outdated
 * (replaced by Design (QP)); it stays in the full list only so a line that stored it before
 * still shows it — a New Product line never offers it.
 */
export const DESIGN_LOCATION_NONE = 'NO Design'
export const DESIGN_LOCATION_QP = 'Design (QP)'
export const DESIGN_LOCATION_TSP_LEGACY = 'Design TSP'
export const NEW_PRODUCT_DESIGN_LOCATION_VALUES: readonly string[] = [
  DESIGN_LOCATION_NONE,
  DESIGN_LOCATION_QP,
]
export const PRODUCTION_LOCATION_BOTH = 'Both Locations'
export const PRODUCTION_LOCATION_EVERETT = 'Everett, WA'
export const PRODUCTION_LOCATION_LANCASTER = 'Lancaster, PA'
export const PRODUCTION_LOCATION_VALUES: readonly string[] = [
  PRODUCTION_LOCATION_BOTH,
  PRODUCTION_LOCATION_EVERETT,
  PRODUCTION_LOCATION_LANCASTER,
]

/**
 * F-064 — a New Product line: a custom (Sign Builder) line, not a change request on a
 * catalog product. Catalog / Standard-Custom order lines and modify lines are not.
 */
export function isNewProductLine(line: { productType: string; modify?: unknown }): boolean {
  return line.productType === 'custom' && !line.modify
}

/** F-038 — the exact code of a value (in the field's current wording), or null when it has none. */
export function attributeValueCode(field: AttributeField, value: string): string | null {
  if (!field.codes) return null
  const code = field.codes[canonicalizeAttributeValue(field, value)]
  return code ? code : null
}

/**
 * A stored / filled value in the field's current wording: multi values are joined
 * canonically (legacy parts matched by code), single values go through `aliases`. Anything
 * else is returned as it is — never dropped.
 */
export function canonicalizeAttributeValue(field: AttributeField, value: string): string {
  if (field.multi && field.values) return canonicalizeMulti(value, field.values)
  if (field.aliases && Object.prototype.hasOwnProperty.call(field.aliases, value)) {
    return field.aliases[value]!
  }
  return value
}

/** Companion attribute holding the catalog id for a catalog-backed field. */
export function catalogIdKey(key: string): string {
  return `${key}_id`
}

/**
 * Whether a (fill or stored) value is one the field's list allows. Free-text fields allow
 * anything; a multi field allows a value when every part is a listed option.
 */
export function attributeValueAllowed(field: AttributeField, value: string): boolean {
  if (!field.values) return true
  if (field.allowOther) return true
  if (field.multi) {
    const parts = splitMulti(value)
    return parts.length > 0 && parts.every((p) => field.values!.includes(p))
  }
  return field.values.includes(value)
}

export interface AttributeSection {
  label: string
  customer?: string[]
  fields: AttributeField[]
}

export type AttributeSchema = Record<string, AttributeSection>

/**
 * The schema sections for one line: customer-scoped sections filtered, and on a New Product
 * line (F-064) every field with `newProductValues` offers that shorter list — plus the value
 * already stored on the line when it is outside it (recorded before the list was limited),
 * so nothing stored is ever hidden behind "— select —".
 */
export function attributeSectionsFor(scope: {
  customer: string
  newProduct: boolean
  stored?: Record<string, string> | null
}): AttributeSection[] {
  const sections = Object.values(ATTRIBUTE_SCHEMA).filter(
    (sec) => !sec.customer || sec.customer.includes(scope.customer)
  )
  if (!scope.newProduct) return sections
  return sections.map((sec) => ({
    ...sec,
    fields: sec.fields.map((field) => {
      if (!field.newProductValues) return field
      const values = [...field.newProductValues]
      const current = scope.stored?.[field.key]
      if (current && !values.includes(current) && field.values?.includes(current)) {
        values.push(current)
      }
      return { ...field, values }
    }),
  }))
}

export const ATTRIBUTE_SCHEMA: AttributeSchema = {
  identity: {
    label: 'Identity',
    fields: [
      { key: 'label', label: 'Description', source: 'auto-intake' },
      { key: 'sku', label: 'SKU', source: 'auto-intake' },
      { key: 'uin', label: 'UIN', source: 'auto-intake' },
      { key: 'price', label: 'Price', source: 'manual' },
      { key: 'reference_sku', label: 'Reference SKU', source: 'auto-intake' },
      { key: 'marketplace', label: 'Marketplace', source: 'auto-intake' },
      {
        key: 'std_type',
        label: 'Standardization Type',
        source: 'auto-customer',
        values: ['Standard', 'Standard-Custom', 'Controlled Custom', 'Complex Custom'],
        // F-064 — a New Product Request only ever becomes Standard or Standard-Custom.
        newProductValues: [...NEW_PRODUCT_STD_TYPE_VALUES],
      },
    ],
  },
  format: {
    label: 'Format & Dimensions',
    fields: [
      { key: 'width', label: 'Width', source: 'auto-intake' },
      { key: 'height', label: 'Height', source: 'auto-intake' },
      // DD-PIECES-001 (F-074 interim): pieces per sign (e.g. 2 A-frame inserts), filled from
      // the Sign Builder, editable here so D&D can set it when Sales did not.
      { key: 'pieces', label: 'Pieces', source: 'auto-intake' },
      {
        // F-063 / F-098 / F-102 — the product's order minimum: Sales picks 25 / 50 / 100 /
        // custom on the New Product form, D&D confirms or changes it here (same list + Other…).
        key: 'min_qty',
        label: 'Minimum quantity',
        source: 'auto-intake',
        values: [...SIGN_BUILDER_MINIMUM_QUANTITY_PRESETS],
        allowOther: true,
      },
      {
        key: 'single_double',
        label: 'Single / Double-sided',
        source: 'auto-intake',
        values: ['Single-sided', 'Double-sided', 'No Print'],
      },
      {
        key: 'format_size',
        label: 'Large / Small Format',
        source: 'auto-customer',
        values: ['Large Format', 'Small Format'],
      },
      { key: 'special_order_tier', label: 'Special Order Tier', source: 'auto-intake' },
    ],
  },
  substrate: {
    label: 'Substrate & Build',
    fields: [
      { key: 'base_substrate', label: 'Base Substrate', source: 'auto-intake', catalog: 'substrate' },
      {
        key: 'category',
        label: 'Category',
        source: 'auto-intake',
        values: SIGN_CATEGORIES.map((c) => c.label),
        allowOther: true,
      },
      {
        key: 'vended',
        label: 'Vended / In-House',
        source: 'auto-customer',
        values: ['Vended', 'In-House'],
      },
      {
        // DD-LOC-RULES-001 — derived from the build on a New Product line (locationRules.ts).
        key: 'prod_loc',
        label: 'Production Location',
        source: 'auto-customer',
        values: [...PRODUCTION_LOCATION_VALUES],
      },
      {
        // DD-LOC-RULES-001 — follows the Standardization Type on a New Product line.
        key: 'design_loc',
        label: 'Design Location',
        source: 'auto-customer',
        values: [DESIGN_LOCATION_NONE, DESIGN_LOCATION_QP, DESIGN_LOCATION_TSP_LEGACY],
        newProductValues: [...NEW_PRODUCT_DESIGN_LOCATION_VALUES],
      },
    ],
  },
  graphics: {
    label: 'Graphics',
    fields: [
      {
        // DD-ATTR-OPTIONS-001 — the options ARE the Sign Builder labels (GRAPHIC_METHODS):
        // the schema used to carry shorter copies ("Direct Print — 1st Surface" vs the
        // Sign Builder's "… (Front)"), so the intake value was rejected by the `values`
        // filter and Graphic Method came up empty for five of the seven methods. Lines
        // that stored the short wording are mapped back through `aliases`.
        key: 'graphic_method',
        label: 'Graphic Method',
        source: 'auto-intake',
        values: GRAPHIC_METHODS.map((g) => g.label),
        aliases: {
          'Direct Print — 1st Surface': 'Direct Print — 1st Surface (Front)',
          'Direct Print — 2nd Surface': 'Direct Print — 2nd Surface (Back/Reverse)',
          'RTA (Ready-to-Apply)': 'RTA (Ready-to-Apply Vinyl)',
          'Cut Vinyl Lettering': 'Cut Vinyl Lettering (Plotted)',
          'Estimating & Design to Decide': 'Estimating & Design Teams to Decide',
        },
      },
      {
        key: 'vinyl_type',
        label: 'Vinyl Type',
        source: 'auto-intake',
        values: VINYL_TYPES.map((v) => v.label),
        allowOther: true,
        codes: codesOf(VINYL_TYPES),
      },
      {
        key: 'rta_color',
        label: 'RTA Color',
        source: 'auto-intake',
        values: ['Black', 'White', 'Prime Blue', 'Reflective Black'],
      },
      {
        key: 'white_ink',
        label: 'White Ink',
        source: 'auto-intake',
        multi: true,
        values: Object.values(WHITE_INK_LABELS),
      },
    ],
  },
  finishing: {
    label: 'Finishing',
    fields: [
      {
        key: 'laminate',
        label: 'Laminate',
        source: 'auto-intake',
        values: LAMINATES.map((l) => l.label),
        allowOther: true,
        codes: codesOf(LAMINATES),
      },
      {
        key: 'taping',
        label: 'Application Tape',
        source: 'auto-intake',
        values: nonEmpty(TAPING_OPTIONS),
        allowOther: true,
      },
      {
        key: 'grommets',
        label: 'Grommets',
        source: 'auto-intake',
        values: nonEmpty(GROMMET_OPTIONS),
        allowOther: true,
      },
      { key: 'hem', label: 'Hem', source: 'auto-intake', values: nonEmpty(HEM_OPTIONS), allowOther: true },
      { key: 'pole_pocket', label: 'Pole Pocket', source: 'auto-intake', values: ['Yes'] },
      {
        key: 'rounded_corners',
        label: 'Rounded Corners',
        source: 'auto-intake',
        values: ROUNDED_CORNER_VALUES,
        allowOther: true,
      },
      {
        key: 'mounting_holes',
        label: 'Mounting Holes',
        source: 'auto-intake',
        values: MOUNTING_HOLE_VALUES,
        allowOther: true,
      },
      // DD-ATTR-FINISHING-001 (F-088): the hardware the packet prints, as an attribute.
      { key: 'hardware', label: 'Hardware', source: 'auto-intake' },
    ],
  },
  amazon: {
    label: 'Amazon Attributes',
    customer: ['Amazon'],
    fields: [
      {
        // DD-AMZ-BU-001 — was the ten legacy PIM "BU" subcategories, one pick; now the
        // Initiatives team's list (amazonBusinessUnits.ts), several picks. Old stored values
        // are matched by code (attributeMulti.canonicalizeParts) so they still show.
        key: 'amz_bu',
        label: 'Business Unit',
        source: 'manual',
        multi: true,
        values: [...AMAZON_BUSINESS_UNIT_OPTIONS],
      },
      {
        key: 'amz_init',
        label: 'Initiative',
        source: 'manual',
        values: ['FLEX', 'Safe to Go', 'DSP - Delivery Service Partner', 'WHS | Workplace Health & Safety'],
      },
      {
        key: 'amz_ptype',
        label: 'Product Type',
        source: 'manual',
        values: ['Adhesive Vinyls/Decals', 'Floor Graphics', 'Banners', 'Flags', 'Canopy Tents'],
      },
    ],
  },
}
